/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React, { useMemo, useState } from 'react'

import { saveAs } from 'file-saver'
import { Link } from 'react-router-dom'

import { Alert, Checkbox, ConfirmModal, PageContainer, PageHeader } from '@/components/ui'
import { useApi } from '@/hooks'
import useCompany from '@/hooks/useCompany'
import logger from '@/utils/logger'

import { ImportResults } from './ImportResults'
import { ImportReviewTable } from './ImportReviewTable'
import { COMPANY_LIST_ID } from './ImportRowEditor'
import { TEMPLATE_HEADERS, parseImportFile, readFileBytes } from './parseImportFile'
import { WRITES, type ImportCommitResult, type ImportRowInput, type ImportRowResult } from './types'

import { LoadingPanel } from '../LoadingPanel'

const log = logger({ category: 'AdminUserImportPage' })

/** Rows per commit request — the server's cap. Smaller chunks mean finer progress. */
const COMMIT_CHUNK = 25

type Filter = 'all' | 'attention' | 'ready' | 'skip'

const FILTERS: { key: Filter; label: string; matches: (verdict: ImportRowResult) => boolean }[] = [
  { key: 'all', label: 'All', matches: () => true },
  { key: 'attention', label: 'Needs attention', matches: (v) => v.action === 'error' },
  { key: 'ready', label: 'Ready', matches: (v) => WRITES.includes(v.action) },
  { key: 'skip', label: 'Skipped', matches: (v) => v.action === 'skip' },
]

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`

const downloadTemplate = () =>
  saveAs(new Blob([`${TEMPLATE_HEADERS.join(',')}\n`], { type: 'text/csv;charset=utf-8' }), 'user-import-template.csv')

/** The local result for a row the import didn't send to the server. */
const notSent = (row: ImportRowInput, verdict: ImportRowResult): ImportCommitResult => ({
  row: verdict.row,
  email: row.email,
  outcome: 'skipped',
  message:
    verdict.action === 'error'
      ? `Not imported: ${verdict.issues.find((issue) => issue.level === 'error')?.message ?? 'needs attention'}`
      : verdict.issues[0]?.message,
})

/**
 * Bulk user import (MEGA-734). Upload a CSV, review what each row would do — new
 * account, SSO placeholder, access for an existing Cooltrax account, or skip —
 * fix what needs fixing, then import. The server judges every row; this page only
 * parses, edits and shows.
 */
const AdminUserImportPage: React.FC = () => {
  const { api } = useApi()
  const { companies } = useCompany()

  const [fileName, setFileName] = useState('')
  const [rows, setRows] = useState<ImportRowInput[]>([])
  const [verdicts, setVerdicts] = useState<ImportRowResult[] | null>(null)
  const [evaluating, setEvaluating] = useState(false)
  const [error, setError] = useState('')
  const [filter, setFilter] = useState<Filter>('all')
  const [editing, setEditing] = useState<number | null>(null)
  const [sendInvites, setSendInvites] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null)
  const [results, setResults] = useState<ImportCommitResult[] | null>(null)

  /**
   * Check a new version of the rows. Rows and verdicts change together, and only
   * once the server answers — a failed re-check leaves the last good state on screen.
   */
  const evaluate = async (next: ImportRowInput[]) => {
    if (next.length === 0) {
      setEditing(null)
      setRows([])
      setVerdicts(null)
      return
    }
    setEvaluating(true)
    setError('')
    try {
      const response = await api.post<ImportRowResult[], { rows: ImportRowInput[] }>({
        path: '/app/admin/users/import/evaluate',
        origin: 'AdminUserImportPage#evaluate',
        body: { rows: next },
      })
      if (response.error || !response.data) {
        setError(response.message || 'Could not check the rows')
        return
      }
      setRows(next)
      setVerdicts(response.data)
      setEditing(null)
    } catch (ex) {
      log.error(ex)
      setError('Could not check the rows')
    } finally {
      setEvaluating(false)
    }
  }

  const chooseFile = async (file: File | undefined) => {
    if (!file) return
    setResults(null)
    setVerdicts(null)
    setFilter('all')
    setFileName(file.name)
    let parsed
    try {
      parsed = parseImportFile(await readFileBytes(file))
    } catch (ex) {
      log.error(ex)
      parsed = { ok: false as const, error: 'The file could not be read.' }
    }
    if (!parsed.ok) {
      setRows([])
      setError(parsed.error)
      return
    }
    setRows([])
    await evaluate(parsed.rows)
  }

  const counts = useMemo(() => {
    const all = verdicts ?? []
    return {
      attention: all.filter((v) => v.action === 'error').length,
      create: all.filter((v) => v.action === 'create' || v.action === 'createSso').length,
      grant: all.filter((v) => v.action === 'grant').length,
      skip: all.filter((v) => v.action === 'skip').length,
    }
  }, [verdicts])
  const writes = counts.create + counts.grant

  const visible = useMemo(() => {
    const matches = FILTERS.find((f) => f.key === filter)?.matches ?? (() => true)
    return (verdicts ?? []).filter(matches).map((v) => v.row)
  }, [verdicts, filter])

  const runImport = async () => {
    if (!verdicts) return
    setConfirming(false)
    const indices = verdicts.filter((v) => WRITES.includes(v.action)).map((v) => v.row)
    const collected: ImportCommitResult[] = []
    setProgress({ done: 0, total: indices.length })
    for (let start = 0; start < indices.length; start += COMMIT_CHUNK) {
      const chunk = indices.slice(start, start + COMMIT_CHUNK)
      try {
        const response = await api.post<ImportCommitResult[], object>({
          path: '/app/admin/users/import',
          origin: 'AdminUserImportPage#import',
          body: { rows, indices: chunk, sendInvites },
        })
        if (response.error || !response.data) throw new Error(response.message || 'The import request failed')
        collected.push(...response.data)
      } catch (ex) {
        log.error(ex)
        // The server may have written some of these before failing — say so rather than guess.
        const message = `${(ex as Error).message} — check the user list before retrying this row`
        collected.push(...chunk.map((row) => ({ row, email: rows[row].email, outcome: 'failed' as const, message })))
      }
      setProgress({ done: Math.min(start + COMMIT_CHUNK, indices.length), total: indices.length })
    }
    const sent = new Set(indices)
    const rest = verdicts.filter((v) => !sent.has(v.row)).map((v) => notSent(rows[v.row], v))
    setResults([...collected, ...rest].sort((a, b) => a.row - b.row))
    setProgress(null)
  }

  const startOver = () => {
    setRows([])
    setVerdicts(null)
    setResults(null)
    setFileName('')
    setError('')
    setSendInvites(false)
  }

  return (
    <PageContainer>
      <Link to="/admin/users" className="link link-hover mb-2 inline-block text-sm">
        ← Users
      </Link>
      <PageHeader title="Import Users" subtitle="Create or give access to many users from a CSV" />

      <datalist id={COMPANY_LIST_ID}>
        {companies.map((company) => (
          <option key={company.id} value={company.name} />
        ))}
      </datalist>

      {results ? (
        <ImportResults results={results} sourceName={fileName} onStartOver={startOver} />
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-3 rounded-lg bg-base-100 p-4 shadow">
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-xs uppercase tracking-wide text-base-content/50">CSV or Excel file</span>
              <input
                type="file"
                accept=".csv,.xlsx,.xls,text/csv"
                aria-label="CSV or Excel file"
                className="file-input file-input-bordered file-input-sm"
                disabled={evaluating || progress !== null}
                onChange={(e) => {
                  void chooseFile(e.target.files?.[0])
                  e.target.value = ''
                }}
              />
            </label>
            <p className="max-w-xl text-xs text-base-content/60">
              Columns: {TEMPLATE_HEADERS.join(', ')}. Leave <em>dashboard</em> blank for the default dashboard, or put
              the email of a user whose dashboards to copy. Username isn&apos;t needed for SSO users.
            </p>
            <button type="button" className="btn btn-ghost btn-sm ml-auto" onClick={downloadTemplate}>
              Download template
            </button>
          </div>

          {error && <Alert type="error" message={error} />}

          {evaluating && !verdicts && <LoadingPanel label="Checking rows…" />}

          {verdicts && (
            <>
              <div className="flex flex-wrap items-center gap-2">
                {FILTERS.map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    className={`btn btn-sm ${filter === f.key ? 'btn-active' : 'btn-ghost'}`}
                    aria-pressed={filter === f.key}
                    onClick={() => setFilter(f.key)}
                  >
                    {f.label} ({verdicts.filter(f.matches).length})
                  </button>
                ))}
                {evaluating && <span className="loading loading-spinner loading-sm" aria-label="Re-checking" />}
              </div>

              <ImportReviewTable
                rows={rows}
                verdicts={verdicts}
                visible={visible}
                editing={editing}
                onEdit={setEditing}
                onSave={(index, updated) => void evaluate(rows.map((row, i) => (i === index ? updated : row)))}
                onRemove={(index) => void evaluate(rows.filter((_, i) => i !== index))}
              />

              <div className="sticky bottom-0 flex flex-wrap items-center gap-4 rounded-lg bg-base-100 p-4 shadow">
                <span className="text-sm" data-testid="import-summary">
                  {plural(counts.create, 'new account')} · {plural(counts.grant, 'existing user')} given access ·{' '}
                  {counts.skip} skipped
                  {counts.attention > 0 && (
                    <span className="text-error"> · {counts.attention} need attention and won&apos;t be imported</span>
                  )}
                </span>
                <Checkbox
                  id="send-invites"
                  label="Send invite emails"
                  checked={sendInvites}
                  onChange={setSendInvites}
                />
                <button
                  type="button"
                  className="btn btn-primary btn-sm ml-auto"
                  disabled={writes === 0 || evaluating || progress !== null}
                  onClick={() => setConfirming(true)}
                >
                  {progress ? `Importing ${progress.done} / ${progress.total}…` : `Import ${plural(writes, 'user')}`}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      <ConfirmModal
        show={confirming}
        title={`Import ${plural(writes, 'user')}?`}
        confirmLabel="Import"
        onCancel={() => setConfirming(false)}
        onConfirm={() => void runImport()}
        body={
          <div className="flex flex-col gap-2 text-sm">
            <p>
              This creates {plural(counts.create, 'new account')} and gives {plural(counts.grant, 'existing user')}{' '}
              access to Insights.
              {counts.attention > 0 && ` ${counts.attention} rows that need attention will be left out.`}
            </p>
            {sendInvites ? (
              <p className="font-semibold text-warning">
                This will send {plural(writes, 'invite email')} now. Users who haven&apos;t heard about Insights yet
                will get them straight away.
              </p>
            ) : (
              <p>
                No emails will be sent. Afterwards, download the results to get each new user&apos;s set-password link
                (valid 7 days).
              </p>
            )}
          </div>
        }
      />
    </PageContainer>
  )
}

export default AdminUserImportPage
